import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Tooltip,
  Box,
  Typography,
  Divider,
  Avatar,
  Checkbox,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RepeatIcon from "@mui/icons-material/Repeat";
import { StrategyActionsMenu } from "./StrategyActionsMenu";
import type { StrategyAction } from "./StrategyActionsMenu";
import type { StrategyListItemV2 } from "../../types/strategy";
import { getStateColor, getStateDisplayNameKo } from "../../types/strategy";
import { getExchangeLogo, EXCHANGE_NAMES } from "../../utils/exchangeUrls";
import { formatVolume as formatVolumeUtil } from "../../utils/formatters";
import { formatConfiguredFundingRate } from "../../utils/futuresFormatters";
import { traderTheme as theme } from "./traderDashboardTheme";
import { useMarketWarnings } from "../../contexts/MarketWarningsContext";
import { MarketWarningBadge } from "../MarketWarningBadge/MarketWarningBadge";

function getExchangeIndex(
  exchangeName: string | undefined | null,
): number | undefined {
  if (!exchangeName) return undefined;
  const normalized = exchangeName
    .toLowerCase()
    .replace(".", "")
    .replace("-", "");
  const index = EXCHANGE_NAMES.findIndex((name) => name === normalized);
  return index >= 0 ? index : undefined;
}

function formatPositionVolume(volume: number | undefined | null): string {
  if (volume === undefined || volume === null) return "-";
  if (volume === 0) return "0";
  return formatVolumeUtil(Math.abs(volume), 6);
}

function formatProfit(profit: number | undefined | null): string {
  if (profit === undefined || profit === null) return "N/A";
  const sign = profit >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(profit).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPercent(value: number | undefined | null, digits = 4): string {
  if (value === undefined || value === null) return "N/A";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(digits)}%`;
}

function formatUsdt(price: number | undefined | null): string {
  if (price === undefined || price === null) return "-";
  return `$${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
}

function formatFundingIntervalLabel(
  intervalHours: number | null | undefined,
): string {
  if (intervalHours === 0) return "any";
  return `${intervalHours ?? 8}h`;
}

function formatQuoteValue(
  currency: string | null,
  value: number | null | undefined,
): string {
  if (!currency || value === null || value === undefined) return "-";
  const prefix = currency === "KRW" ? "₩" : currency === "BTC" ? "₿" : "";
  const maximumFractionDigits = currency === "KRW" ? 2 : 8;
  return `${prefix}${value.toLocaleString("en-US", { maximumFractionDigits })}`;
}

function quoteToUsdtRate(strategy: StrategyListItemV2): number | null {
  if (!strategy.spot_is_non_usdt_quote) return 1;
  if (
    strategy.spot_current_price_quote &&
    strategy.spot_current_price_usdt &&
    strategy.spot_current_price_quote !== 0
  ) {
    return strategy.spot_current_price_usdt / strategy.spot_current_price_quote;
  }
  return null;
}

function useElapsedTime(startTime: number | null | undefined): string {
  const [elapsed, setElapsed] = useState("");

  useEffect(() => {
    if (!startTime) {
      setElapsed("");
      return;
    }

    const update = () => {
      const diff = Math.max(0, Date.now() - startTime);
      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      if (days > 0) {
        setElapsed(hours > 0 ? `${days}d ${hours}h` : `${days}d`);
      } else if (hours > 0) {
        setElapsed(minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`);
      } else {
        setElapsed(`${minutes}m`);
      }
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [startTime]);

  return elapsed;
}

function useFundingTimer(nextFundingTime: number | null): string {
  const [timer, setTimer] = useState("00:00:00");

  useEffect(() => {
    const updateTimer = () => {
      const now = Date.now();

      let nextFunding: number;
      if (nextFundingTime && nextFundingTime > now) {
        nextFunding = nextFundingTime;
      } else {
        const nowDate = new Date();
        const hours = nowDate.getUTCHours();
        const nextFundingHour = Math.ceil(hours / 8) * 8;
        const nextFundingDate = new Date(nowDate);
        nextFundingDate.setUTCHours(nextFundingHour, 0, 0, 0);
        if (nextFundingDate.getTime() <= now) {
          nextFundingDate.setUTCHours(nextFundingHour + 8, 0, 0, 0);
        }
        nextFunding = nextFundingDate.getTime();
      }

      const diff = Math.max(0, nextFunding - now);
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);
      setTimer(
        `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`,
      );
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [nextFundingTime]);

  return timer;
}

const subTextStyle = {
  fontSize: { xs: "0.7rem", md: "0.8rem" },
  color: "text.secondary",
  mt: 0.25,
};

export interface PositionRowV2Props {
  strategy: StrategyListItemV2;
  onAction: (strategyId: string, action: StrategyAction) => void;
  loadingAction?: StrategyAction | null;
  onSymbolClick?: (baseAsset: string) => void;
  onHoverStart?: (strategyId: string) => void;
  onHoverEnd?: () => void;
  isHovered?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (strategyId: string, selected: boolean) => void;
}

export const PositionRowV2: React.FC<PositionRowV2Props> = React.memo(
  ({
    strategy,
    onAction,
    loadingAction,
    onSymbolClick,
    onHoverStart,
    onHoverEnd,
    isHovered,
    isSelected = false,
    onSelectionChange,
  }) => {
    const { getWarnedVendors } = useMarketWarnings();
    const symbol = strategy.symbol || "-";
    const futuresPriceDataComplete =
      strategy.futures_price_data_complete !== false;
    const spotExchangeIndex = getExchangeIndex(strategy.spot_exchange_name);
    const fundingRate =
      strategy.funding_rate !== null ? strategy.funding_rate * 100 : null;
    const fundingTimer = useFundingTimer(strategy.next_funding_time);
    const fundingIntervalHours = useMemo(() => {
      if (
        strategy.next_funding_time &&
        strategy.previous_funding_time &&
        strategy.previous_funding_time > 0
      ) {
        return Math.round(
          (strategy.next_funding_time - strategy.previous_funding_time) /
            3600000,
        );
      }
      return null;
    }, [strategy.next_funding_time, strategy.previous_funding_time]);
    const elapsedTime = useElapsedTime(
      strategy.detected_at || strategy.started_at,
    );
    const rowIndex = strategy.strategy_id.charCodeAt(0) % 2;
    const bgColor = rowIndex === 0 ? theme.bg.secondary : theme.bg.primary;
    const spotQuoteRate = quoteToUsdtRate(strategy);
    const spotBestAskUsdt =
      strategy.spot_best_ask != null
        ? strategy.spot_best_ask * (spotQuoteRate ?? 1)
        : null;
    const warningVendors = getWarnedVendors([
      {
        exchange: strategy.spot_exchange_name,
        quote: strategy.spot_quote_currency?.trim() || strategy.buy_quote,
        base: symbol,
      },
    ]);

    const buyOpenProgress = useMemo(() => {
      if (strategy.current_state !== "BuyOpen") return null;
      const budget = strategy.budget_usdt || 0;
      const spotSpent =
        (strategy.spot_avg_price_usdt ?? 0) * strategy.spot_total_volume;
      const futuresSpent =
        strategy.futures_avg_price_usdt == null
          ? null
          : strategy.futures_avg_price_usdt * strategy.futures_total_volume;
      return {
        budget,
        spotSpent,
        futuresSpent,
        spotPercent: budget > 0 ? (spotSpent / budget) * 100 : 0,
        futuresPercent:
          futuresSpent == null
            ? null
            : budget > 0
              ? (futuresSpent / budget) * 100
              : 0,
      };
    }, [strategy]);

    const getPnlColor = (value: number | null | undefined) => {
      if (value === null || value === undefined) return theme.text.secondary;
      return value >= 0 ? theme.profit : theme.loss;
    };
    const netRealizedPnl =
      strategy.futures_realized_pnl_usdt == null
        ? null
        : strategy.spot_realized_pnl_usdt +
          strategy.futures_realized_pnl_usdt;

    const remainingTiersText = useMemo(
      () =>
        strategy.remaining_profit_tiers
          .map(
            (tier, index) =>
              `${index > 0 ? " " : ""}${(tier.profit_percentage * 100).toFixed(1)}%→${(tier.volume_percentage_to_sell * 100).toFixed(0)}%`,
          )
          .join(""),
      [strategy.remaining_profit_tiers],
    );
    const filledTiersText = useMemo(
      () =>
        strategy.filled_profit_tiers
          .map(
            (tier, index) =>
              `${index > 0 ? " " : ""}${(tier.profit_percentage * 100).toFixed(1)}%→${(tier.volume_percentage_to_sell * 100).toFixed(0)}%`,
          )
          .join(""),
      [strategy.filled_profit_tiers],
    );

    const balanceTooltip = (
      <Box sx={{ p: 1.5, minWidth: 280 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", mb: 1, color: "common.white" }}
        >
          Balance Breakdown
        </Typography>
        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.1)" }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: theme.price.spot,
              display: "block",
              mb: 0.5,
            }}
          >
            Spot Purchased ({formatVolumeUtil(strategy.spot_total_volume, 4)}{" "}
            {symbol})
          </Typography>
          <Box sx={{ pl: 1, mb: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Cost Basis:
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                {strategy.spot_is_non_usdt_quote && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.text.secondary,
                      fontSize: "0.7rem",
                      display: "block",
                      lineHeight: 1,
                    }}
                  >
                    {formatQuoteValue(
                      strategy.spot_quote_currency,
                      (strategy.spot_avg_price_quote ?? 0) *
                        strategy.spot_total_volume,
                    )}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                  {formatUsdt(
                    (strategy.spot_avg_price_usdt ?? 0) *
                      strategy.spot_total_volume,
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: theme.price.spot,
              display: "block",
              mb: 0.5,
            }}
          >
            Spot Remaining (
            {formatVolumeUtil(strategy.spot_remaining_volume, 4)} {symbol})
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Cost Basis:
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                {strategy.spot_is_non_usdt_quote && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.text.secondary,
                      fontSize: "0.7rem",
                      display: "block",
                      lineHeight: 1,
                    }}
                  >
                    {formatQuoteValue(
                      strategy.spot_quote_currency,
                      (strategy.spot_avg_price_quote ?? 0) *
                        strategy.spot_remaining_volume,
                    )}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                  {formatUsdt(strategy.spot_cost_basis_usdt)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Current Value:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {formatUsdt(strategy.spot_current_value_usdt)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: theme.price.futures,
              display: "block",
              mb: 0.5,
            }}
          >
            Futures Shorted (
            {formatVolumeUtil(strategy.futures_total_volume, 4)} {symbol})
          </Typography>
          <Box sx={{ pl: 1, mb: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Entry Value:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {formatUsdt(
                  strategy.futures_avg_price_usdt == null
                    ? null
                    : strategy.futures_avg_price_usdt *
                        strategy.futures_total_volume,
                )}
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: theme.price.futures,
              display: "block",
              mb: 0.5,
            }}
          >
            Futures Remaining (
            {formatVolumeUtil(Math.abs(strategy.futures_remaining_volume), 4)}{" "}
            {symbol})
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Entry Value:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {formatUsdt(
                  strategy.futures_avg_price_usdt == null
                    ? null
                    : strategy.futures_avg_price_usdt *
                        Math.abs(strategy.futures_remaining_volume),
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Current Value:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {formatUsdt(
                  (strategy.futures_current_price_usdt ?? 0) *
                    Math.abs(strategy.futures_remaining_volume),
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );

    const filledVolumeTooltip = (
      <Box sx={{ p: 1.5, minWidth: 280 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", mb: 1, color: "common.white" }}
        >
          Realized PnL (Filled)
        </Typography>
        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.1)" }} />

        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", color: "rgba(255,255,255,0.9)", pl: 1 }}
            >
              Net Realized:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: "bold",
                color: getPnlColor(netRealizedPnl),
                fontFamily: "monospace",
              }}
            >
              {formatProfit(netRealizedPnl)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.1)" }} />

        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: theme.price.spot,
              display: "block",
              mb: 0.5,
            }}
          >
            Spot Sold ({formatVolumeUtil(strategy.spot_asked_volume, 4)}{" "}
            {symbol})
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Cost Basis:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {formatUsdt(
                  (strategy.spot_avg_price_usdt ?? 0) *
                    strategy.spot_asked_volume,
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Realized PnL:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    strategy.spot_realized_pnl_usdt >= 0
                      ? theme.profit
                      : theme.loss,
                  fontFamily: "monospace",
                }}
              >
                {formatProfit(strategy.spot_realized_pnl_usdt)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: theme.price.futures,
              display: "block",
              mb: 0.5,
            }}
          >
            Futures Closed (
            {formatVolumeUtil(strategy.futures_closed_volume, 4)} {symbol})
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Entry Value:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {formatUsdt(
                  strategy.futures_avg_price_usdt == null
                    ? null
                    : strategy.futures_avg_price_usdt *
                        strategy.futures_closed_volume,
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Realized PnL:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: getPnlColor(strategy.futures_realized_pnl_usdt),
                  fontFamily: "monospace",
                }}
              >
                {formatProfit(strategy.futures_realized_pnl_usdt)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );

    const profitTooltip = (
      <Box sx={{ p: 1.5, minWidth: 250 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", mb: 1, color: "common.white" }}
        >
          PnL Analysis
        </Typography>
        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.1)" }} />

        <Box
          sx={{
            mb: 1.5,
            bgcolor: "rgba(255,255,255,0.05)",
            p: 1,
            borderRadius: 1,
          }}
        >
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", color: "common.white" }}
            >
              Net Profit:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: "bold",
                color: getPnlColor(strategy.net_profit_usdt),
                fontFamily: "monospace",
              }}
            >
              {formatProfit(strategy.net_profit_usdt)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.7)" }}
            >
              ROI:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: getPnlColor(strategy.roi_percent),
                fontFamily: "monospace",
              }}
            >
              {formatPercent(strategy.roi_percent)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.1)" }} />

        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: theme.price.spot,
              display: "block",
              mb: 0.5,
            }}
          >
            Spot Side
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Current Val:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {formatUsdt(strategy.spot_current_value_usdt)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Cost Basis:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                {formatUsdt(strategy.spot_cost_basis_usdt)}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", color: "rgba(255,255,255,0.9)" }}
              >
                Spot PnL:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  color:
                    (strategy.spot_profit_usdt ?? 0) >= 0
                      ? theme.profit
                      : theme.loss,
                  fontFamily: "monospace",
                }}
              >
                {formatProfit(strategy.spot_profit_usdt)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.1)" }} />

        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: theme.price.futures,
              display: "block",
              mb: 0.5,
            }}
          >
            Futures Side
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                PnL:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: getPnlColor(strategy.futures_profit_usdt),
                  fontFamily: "monospace",
                }}
              >
                {formatProfit(strategy.futures_profit_usdt)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Funding:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    strategy.total_funding_fees_usdt >= 0
                      ? theme.profit
                      : theme.loss,
                  fontFamily: "monospace",
                }}
              >
                {strategy.total_funding_fees_usdt >= 0 ? "+" : "-"}$
                {Math.abs(strategy.total_funding_fees_usdt).toLocaleString(
                  "en-US",
                  { minimumFractionDigits: 6, maximumFractionDigits: 6 },
                )}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.1)" }} />

        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              color: "text.secondary",
              display: "block",
              mb: 0.5,
            }}
          >
            Fees Paid
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Spot:
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.loss, fontFamily: "monospace" }}
              >
                -{formatUsdt(strategy.spot_fees_usdt).replace("$", "")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Futures:
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.loss, fontFamily: "monospace" }}
              >
                -{formatUsdt(strategy.futures_fees_usdt).replace("$", "")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );

    const handleMouseEnter = useCallback(() => {
      onHoverStart?.(strategy.strategy_id);
    }, [onHoverStart, strategy.strategy_id]);

    const handleMouseLeave = useCallback(() => {
      onHoverEnd?.();
    }, [onHoverEnd]);

    const handleRowClick = useCallback(
      (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        const isInteractive = target.closest(
          'button, a, [role="button"], .MuiIconButton-root, .MuiMenu-root',
        );
        if (isInteractive) return;
        onSelectionChange?.(strategy.strategy_id, !isSelected);
      },
      [strategy.strategy_id, isSelected, onSelectionChange],
    );

    const displayEntryDiff = strategy.entry_diff_ratio * 100;
    const buyOpenDiffPercent =
      spotBestAskUsdt && strategy.futures_best_bid
        ? ((spotBestAskUsdt - strategy.futures_best_bid) / spotBestAskUsdt) *
          100
        : null;
    const supportsReplaySettings =
      (strategy.strategy_type === "StandbyHedged" ||
        strategy.strategy_type === "StandbyHedgedCross") &&
      !["HedgeClose", "Abort", "Success", "Failure"].includes(
        strategy.current_state,
      );
    const supportsEntryFundingRateEdit =
      strategy.current_state === "BuyOpen" && strategy.hedge_config !== null;
    const replayEnabledTooltip =
      strategy.replay_delay_seconds > 0
        ? `Replay enabled after completion · ${strategy.replay_delay_seconds}s delay`
        : "Replay enabled after completion";
    const replayOriginTooltip = strategy.replay_source_strategy_id
      ? `Replay run · source ${strategy.replay_source_strategy_id}`
      : "Replay run";
    const buyOpenEntryFundingThreshold =
      strategy.current_state === "BuyOpen"
        ? strategy.hedge_config?.rebalancing?.entry_funding_rate_threshold
        : null;
    const buyOpenEntryFundingThresholdText =
      buyOpenEntryFundingThreshold != null
        ? formatConfiguredFundingRate(buyOpenEntryFundingThreshold)
        : null;
    const buyOpenEntryFundingIntervalText =
      buyOpenEntryFundingThreshold != null
        ? formatFundingIntervalLabel(
            strategy.hedge_config?.rebalancing?.entry_funding_interval_hours,
          )
        : null;

    return (
      <Box
        onClick={handleRowClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          display: "flex",
          backgroundColor: isSelected
            ? "rgba(88, 166, 255, 0.2)"
            : isHovered
              ? "rgba(88, 166, 255, 0.15)"
              : bgColor,
          py: 1,
          px: 1,
          borderBottom: `1px solid ${theme.border}`,
          transition: "background-color 0.15s ease",
          position: "relative",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: isSelected
              ? "rgba(88, 166, 255, 0.25)"
              : theme.bg.hover,
          },
        }}
      >
        {isSelected && (
          <Box
            sx={{
              position: "absolute",
              left: 4,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Checkbox
              checked
              size="small"
              sx={{
                color: theme.accent,
                "&.Mui-checked": { color: theme.accent },
                padding: 0,
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            />
          </Box>
        )}

        {isHovered && (
          <Box
            sx={{
              position: "absolute",
              left: -4,
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <LockIcon sx={{ fontSize: 14, color: theme.accent }} />
          </Box>
        )}

        <Box
          onClick={onSymbolClick ? () => onSymbolClick(symbol) : undefined}
          sx={{
            width: "12%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            cursor: onSymbolClick ? "pointer" : "default",
            "&:hover": onSymbolClick
              ? { "& .symbol-text": { color: theme.accent } }
              : {},
          }}
        >
          <Box
            sx={{
              minWidth: 20,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {spotExchangeIndex !== undefined && (
              <Avatar
                src={getExchangeLogo(spotExchangeIndex)}
                sx={{ width: 20, height: 20 }}
              />
            )}
            {(strategy.replay_enabled || strategy.is_replay) && (
              <Box
                sx={{
                  mt: 0.35,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.35,
                }}
              >
                {strategy.replay_enabled && (
                  <Tooltip title={replayEnabledTooltip} arrow placement="top">
                    <Box
                      data-testid="replay-enabled-badge"
                      sx={{
                        width: 16,
                        height: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        backgroundColor: "rgba(63, 185, 80, 0.14)",
                        color: theme.profit,
                      }}
                    >
                      <RepeatIcon sx={{ fontSize: 11 }} />
                    </Box>
                  </Tooltip>
                )}
                {strategy.is_replay && (
                  <Tooltip title={replayOriginTooltip} arrow placement="top">
                    <Box
                      data-testid="replay-origin-badge"
                      sx={{
                        width: 13,
                        height: 13,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        backgroundColor: "rgba(88, 166, 255, 0.14)",
                        color: theme.accent,
                      }}
                    >
                      <HourglassEmptyIcon sx={{ fontSize: 9 }} />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MarketWarningBadge vendors={warningVendors} />
              <Typography
                className="symbol-text"
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  color: theme.text.primary,
                  fontFamily: "monospace",
                  transition: "color 0.15s ease",
                  textAlign: "left",
                }}
              >
                {symbol}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.7rem", md: "0.8rem" },
                color: strategy.is_monitoring_paused
                  ? theme.warning
                  : getStateColor(strategy.current_state),
                fontWeight: 500,
                mt: 0.25,
                textAlign: "left",
              }}
            >
              {strategy.is_monitoring_paused
                ? "일시정지"
                : getStateDisplayNameKo(strategy.current_state)}
              {elapsedTime && (
                <Box
                  component="span"
                  sx={{
                    color: theme.text.secondary,
                    ml: 0.5,
                    fontSize: { xs: "0.65rem", md: "0.7rem" },
                  }}
                >
                  {elapsedTime}
                </Box>
              )}
            </Typography>
            {buyOpenEntryFundingThresholdText && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.65rem", md: "0.7rem" },
                  mt: 0.25,
                  textAlign: "left",
                  fontFamily: "monospace",
                }}
              >
                <Box component="span" sx={{ color: theme.text.primary }}>
                  {buyOpenEntryFundingThresholdText}
                </Box>
                {buyOpenEntryFundingIntervalText && (
                  <Box component="span" sx={{ color: theme.text.secondary }}>
                    {` (${buyOpenEntryFundingIntervalText})`}
                  </Box>
                )}
              </Typography>
            )}
            {strategy.current_state === "Standby" &&
              strategy.budget_usdt > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.65rem", md: "0.7rem" },
                    mt: 0.25,
                    textAlign: "left",
                    fontFamily: "monospace",
                  }}
                >
                  <Box component="span" sx={{ color: theme.text.secondary }}>
                    $
                    {strategy.budget_usdt.toLocaleString("en-US", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </Box>{" "}
                  <Box
                    component="span"
                    sx={{
                      color:
                        strategy.entry_diff_ratio < 0
                          ? theme.profit
                          : theme.loss,
                    }}
                  >
                    {displayEntryDiff.toFixed(2)}%
                  </Box>
                </Typography>
              )}
          </Box>
        </Box>

        <Box
          sx={{
            width: "12%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          {strategy.current_state === "BuyOpen" ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                  color: theme.text.primary,
                  fontFamily: "monospace",
                }}
              >
                $
                {strategy.budget_usdt.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                  color:
                    strategy.entry_diff_ratio < 0 ? theme.profit : theme.loss,
                  fontFamily: "monospace",
                  mt: 0.5,
                }}
              >
                {displayEntryDiff.toFixed(2)}%
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.65rem", md: "0.75rem" },
                  color:
                    strategy.buyopen_mode?.type === "Market"
                      ? "#ff9800"
                      : theme.text.secondary,
                  mt: 0.25,
                }}
              >
                {strategy.buyopen_mode?.type === "Market"
                  ? `시장가 $${strategy.buyopen_mode.max_notional_usdt || 0}`
                  : "지정가"}
              </Typography>
            </>
          ) : (
            <>
              {strategy.spot_is_non_usdt_quote &&
              strategy.spot_avg_price_quote ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.65rem", md: "0.75rem" },
                      color: theme.text.secondary,
                    }}
                  >
                    {formatQuoteValue(
                      strategy.spot_quote_currency,
                      strategy.spot_avg_price_quote,
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.75rem", md: "0.85rem" },
                      color: theme.price.spot,
                      fontFamily: "monospace",
                    }}
                  >
                    {formatUsdt(strategy.spot_avg_price_usdt)}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.85rem" },
                    color: theme.price.spot,
                    fontFamily: "monospace",
                  }}
                >
                  {formatUsdt(strategy.spot_avg_price_usdt)}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                  color: theme.price.futures,
                  fontFamily: "monospace",
                  mt: 0.5,
                }}
              >
                {formatUsdt(strategy.futures_avg_price_usdt)}
              </Typography>
            </>
          )}
        </Box>

        <Box
          sx={{
            width: "12%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          {strategy.current_state === "BuyOpen" ? (
            <>
              {strategy.buy_quote === "K" || strategy.buy_quote === "KRW" ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.65rem", md: "0.75rem" },
                      color: theme.text.secondary,
                    }}
                  >
                    {formatQuoteValue("KRW", strategy.spot_best_ask)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.75rem", md: "0.85rem" },
                      color: theme.price.spot,
                      fontFamily: "monospace",
                    }}
                  >
                    {formatUsdt(spotBestAskUsdt)}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.85rem" },
                    color: theme.price.spot,
                    fontFamily: "monospace",
                  }}
                >
                  {formatUsdt(spotBestAskUsdt ?? strategy.spot_best_ask)}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                  color: theme.price.futures,
                  fontFamily: "monospace",
                  mt: 0.5,
                }}
              >
                {formatUsdt(strategy.futures_mark_price)}
              </Typography>
            </>
          ) : (
            <>
              {strategy.spot_is_non_usdt_quote &&
              strategy.spot_current_price_quote ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.65rem", md: "0.75rem" },
                      color: theme.text.secondary,
                    }}
                  >
                    {formatQuoteValue(
                      strategy.spot_quote_currency,
                      strategy.spot_current_price_quote,
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.75rem", md: "0.85rem" },
                      color: theme.price.spot,
                      fontFamily: "monospace",
                    }}
                  >
                    {formatUsdt(strategy.spot_current_price_usdt)}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.85rem" },
                    color: theme.price.spot,
                    fontFamily: "monospace",
                  }}
                >
                  {formatUsdt(strategy.spot_current_price_usdt)}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                  color: theme.price.futures,
                  fontFamily: "monospace",
                  mt: 0.5,
                }}
              >
                {formatUsdt(strategy.futures_current_price_usdt)}
              </Typography>
            </>
          )}
        </Box>

        <Box
          sx={{
            width: "10%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {strategy.current_state === "BuyOpen" ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.8rem", md: "0.9rem" },
                  color:
                    buyOpenDiffPercent !== null
                      ? buyOpenDiffPercent < 0
                        ? theme.profit
                        : theme.loss
                      : theme.text.secondary,
                  fontWeight: 600,
                  fontFamily: "monospace",
                }}
              >
                {buyOpenDiffPercent !== null
                  ? `${buyOpenDiffPercent.toFixed(2)}%`
                  : "-"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.65rem", md: "0.75rem" },
                  color: theme.text.secondary,
                  mt: 0.25,
                }}
              >
                현재 차이
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.8rem", md: "0.9rem" },
                  color: strategy.liquidation_price_usdt
                    ? theme.danger
                    : theme.text.secondary,
                  fontWeight: strategy.liquidation_price_usdt ? 600 : 400,
                  fontFamily: "monospace",
                }}
              >
                {formatUsdt(strategy.liquidation_price_usdt)}
              </Typography>
              {strategy.liquidation_price_usdt &&
                strategy.liquidation_margin_percent !== null && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.65rem", md: "0.75rem" },
                      color:
                        strategy.liquidation_margin_percent > 90
                          ? theme.danger
                          : theme.text.secondary,
                      mt: 0.25,
                      fontFamily: "monospace",
                    }}
                  >
                    {strategy.liquidation_margin_percent.toFixed(1)}%
                  </Typography>
                )}
              {strategy.liquidation_price_usdt &&
                strategy.hedge_config?.leverage != null && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.65rem", md: "0.75rem" },
                      color: theme.text.secondary,
                      mt: 0.25,
                      fontFamily: "monospace",
                    }}
                  >
                    {strategy.hedge_config.leverage}x
                  </Typography>
                )}
            </>
          )}
        </Box>

        <Box
          sx={{
            width: "12%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color:
                  strategy.total_funding_fees_usdt >= 0
                    ? theme.profit
                    : theme.loss,
                fontFamily: "monospace",
                lineHeight: 1.2,
                mb: 0.7,
                opacity: 0.75,
              }}
            >
              {strategy.total_funding_fees_usdt >= 0 ? "+" : "-"}$
              {Math.abs(strategy.total_funding_fees_usdt).toLocaleString(
                "en-US",
                { minimumFractionDigits: 6, maximumFractionDigits: 6 },
              )}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: "0.8rem", md: "0.9rem" },
                color:
                  fundingRate === null
                    ? theme.text.secondary
                    : fundingRate >= 0
                      ? theme.profit
                      : theme.loss,
                fontFamily: "monospace",
                lineHeight: 1.2,
              }}
            >
              {formatPercent(fundingRate)}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              ...subTextStyle,
              fontFamily: "monospace",
              color: theme.text.secondary,
              textAlign: "center",
            }}
          >
            {fundingTimer}
            {fundingIntervalHours !== null && ` (${fundingIntervalHours}h)`}
          </Typography>
        </Box>

        <Box
          sx={{
            width: "6%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Tooltip title={balanceTooltip} arrow placement="top">
            <Box sx={{ cursor: "pointer" }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.8rem", md: "0.9rem" },
                  color: theme.price.spot,
                  fontFamily: "monospace",
                }}
              >
                {formatPositionVolume(strategy.spot_remaining_volume)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ...subTextStyle,
                  color: theme.price.futures,
                  fontFamily: "monospace",
                }}
              >
                {formatPositionVolume(strategy.futures_remaining_volume)}
              </Typography>
              {buyOpenProgress && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.6rem", md: "0.65rem" },
                    fontFamily: "monospace",
                    mt: 0.25,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ color: theme.price.spot, opacity: 0.7 }}
                  >
                    {buyOpenProgress.spotPercent.toFixed(1)}%
                  </Box>{" "}
                  <Box
                    component="span"
                    sx={{ color: theme.price.futures, opacity: 0.7 }}
                  >
                    {buyOpenProgress.futuresPercent == null
                      ? "N/A"
                      : `${buyOpenProgress.futuresPercent.toFixed(1)}%`}
                  </Box>
                </Typography>
              )}
              {!buyOpenProgress && remainingTiersText && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.6rem", md: "0.65rem" },
                    color: theme.text.secondary,
                    fontFamily: "monospace",
                    mt: 0.25,
                  }}
                >
                  {remainingTiersText}
                </Typography>
              )}
            </Box>
          </Tooltip>
        </Box>

        <Box
          sx={{
            width: "6%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Tooltip title={filledVolumeTooltip} arrow placement="top">
            <Box sx={{ cursor: "pointer" }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.8rem", md: "0.9rem" },
                  color: theme.price.spot,
                  fontFamily: "monospace",
                }}
              >
                {formatPositionVolume(strategy.spot_asked_volume)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ...subTextStyle,
                  color: theme.price.futures,
                  fontFamily: "monospace",
                }}
              >
                {formatPositionVolume(strategy.futures_closed_volume)}
              </Typography>
              {filledTiersText && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.6rem", md: "0.65rem" },
                    color: theme.text.secondary,
                    fontFamily: "monospace",
                    mt: 0.25,
                  }}
                >
                  {filledTiersText}
                </Typography>
              )}
            </Box>
          </Tooltip>
        </Box>

        <Box
          sx={{
            width: "20%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "right",
            pr: 4,
          }}
        >
          <Tooltip title={profitTooltip} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.9rem", md: "1rem" },
                fontWeight: 600,
                color: getPnlColor(strategy.net_profit_usdt),
                fontFamily: "monospace",
                cursor: "pointer",
                width: "fit-content",
                alignSelf: "flex-end",
              }}
            >
              {formatProfit(strategy.net_profit_usdt)}
            </Typography>
          </Tooltip>
          <Typography
            variant="body2"
            sx={{
              ...subTextStyle,
              fontFamily: "monospace",
              color: theme.price.spot,
            }}
          >
            {formatProfit(strategy.spot_profit_usdt)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              ...subTextStyle,
              fontFamily: "monospace",
              color: theme.price.futures,
            }}
          >
            {formatProfit(strategy.futures_profit_usdt)}
          </Typography>
          {!futuresPriceDataComplete && (
            <Typography
              data-testid="futures-price-data-incomplete"
              variant="caption"
              sx={{
                color: theme.text.secondary,
                fontSize: { xs: "0.6rem", md: "0.65rem" },
                mt: 0.25,
              }}
            >
              {strategy.futures_price_data_error ?? "price data incomplete"}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: 4,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <StrategyActionsMenu
            strategyId={strategy.strategy_id}
            currentState={strategy.current_state}
            isMonitoringPaused={strategy.is_monitoring_paused}
            supportsActions={
              !["Success", "Failure", "Abort"].includes(strategy.current_state)
            }
            supportsReplaySettings={supportsReplaySettings}
            supportsEntryFundingRateEdit={supportsEntryFundingRateEdit}
            loadingAction={loadingAction}
            onAction={onAction}
          />
        </Box>
      </Box>
    );
  },
);

PositionRowV2.displayName = "PositionRowV2";

export default PositionRowV2;
